import { useState } from 'react';
import { formatPrice } from '../../../utils/format';

interface Paiement {
  total: number;
  remise: number;
  acompte: number;
  modePaiement: string;
}

interface FormulairePaiementProps {
  paiement: Paiement;
  onChange: (field: keyof Paiement, value: string | number) => void;
}

const modes = ['Espèces', 'Orange Money', 'Wave', 'Moov Money', 'Virement'];

export default function FormulairePaiement({ paiement, onChange }: FormulairePaiementProps) {
  const [remiseType, setRemiseType] = useState<'fixe' | 'pourcent'>(paiement.remise > 0 && paiement.remise < 100 ? 'fixe' : 'fixe'); // simple default
  const [remiseValue, setRemiseValue] = useState(paiement.remise || 0);

  const totalAvecRemise = Math.max(0, paiement.total - paiement.remise);
  const reste = Math.max(0, totalAvecRemise - paiement.acompte);

  const handleRemiseChange = (val: number, type: 'fixe' | 'pourcent') => {
    let finalRemise = val;
    if (type === 'pourcent') {
      finalRemise = Math.round((paiement.total * val) / 100);
    }
    setRemiseValue(val);
    onChange('remise', finalRemise);
  };

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Total */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5 uppercase tracking-wider text-[10px]">
            Prix total de base (FCFA) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-money-franc-circle-line" />
            </div>
            <input
              type="number"
              value={paiement.total || ''}
              onChange={e => onChange('total', parseInt(e.target.value) || 0)}
              placeholder="ex: 45000"
              min={0}
              className="w-full pl-9 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 focus:bg-white transition-all font-bold"
            />
          </div>
        </div>

        {/* Remise */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5 uppercase tracking-wider text-[10px]">
            Remise / Réduction
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-scissors-cut-line" />
              </div>
              <input
                type="number"
                value={remiseValue || ''}
                onChange={e => handleRemiseChange(parseInt(e.target.value) || 0, remiseType)}
                placeholder={remiseType === 'fixe' ? "Montant (FCFA)" : "Pourcentage (%)"}
                className="w-full pl-9 pr-4 py-3 text-sm border border-gold-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold-400 bg-gold-50/30 font-bold"
              />
            </div>
            <div className="flex border border-gray-200 rounded-xl overflow-hidden bg-white">
              <button 
                onClick={() => { setRemiseType('fixe'); handleRemiseChange(remiseValue, 'fixe'); }}
                className={`px-3 flex items-center justify-center text-xs font-bold transition-colors cursor-pointer ${remiseType === 'fixe' ? 'bg-gold-500 text-white' : 'hover:bg-gray-50 text-gray-400'}`}
              >
                F
              </button>
              <button 
                onClick={() => { setRemiseType('pourcent'); handleRemiseChange(remiseValue, 'pourcent'); }}
                className={`px-3 flex items-center justify-center text-xs font-bold transition-colors cursor-pointer ${remiseType === 'pourcent' ? 'bg-gold-500 text-white' : 'hover:bg-gray-50 text-gray-400'}`}
              >
                %
              </button>
            </div>
          </div>
        </div>
      </div>

      {paiement.remise > 0 && (
         <div className="flex justify-between items-center px-4 py-2 bg-gold-50 border border-gold-100 rounded-xl">
            <span className="text-xs font-bold text-gold-700 uppercase">Nouveau Total net :</span>
            <span className="text-sm font-bold text-gold-800">{formatPrice(totalAvecRemise)}</span>
         </div>
      )}

      {/* Acompte */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1.5 uppercase tracking-wider text-[10px]">Acompte versé (FCFA)</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-secure-payment-line" />
          </div>
          <input
            type="number"
            value={paiement.acompte || ''}
            onChange={e => onChange('acompte', parseInt(e.target.value) || 0)}
            placeholder="ex: 20000"
            min={0}
            max={totalAvecRemise}
            className="w-full pl-9 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-gray-50 focus:bg-white transition-all font-bold"
          />
        </div>
      </div>

      {/* Reste à payer */}
      {paiement.total > 0 && (
        <div className={`rounded-xl px-4 py-3 border shadow-sm transition-all ${reste > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 flex items-center justify-center ${reste > 0 ? 'text-red-500' : 'text-green-600'}`}>
                <i className={reste > 0 ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'} />
              </div>
              <span className={`text-[10px] uppercase font-bold tracking-wider ${reste > 0 ? 'text-red-700' : 'text-green-700'}`}>
                {reste > 0 ? 'Reste à payer à la livraison' : 'Entièrement payé'}
              </span>
            </div>
            <span className={`text-xl font-black ${reste > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatPrice(reste)}
            </span>
          </div>
        </div>
      )}

      {/* Acompte quick buttons */}
      {totalAvecRemise > 0 && (
        <div>
          <p className="text-[10px] uppercase font-bold text-gray-500 mb-2 tracking-widest">Acompte rapide :</p>
          <div className="flex gap-2 flex-wrap">
            {[0.50, 0.75, 1].map(ratio => (
              <button
                key={ratio}
                onClick={(e) => { e.preventDefault(); onChange('acompte', Math.round(totalAvecRemise * ratio)); }}
                className="px-4 py-2 text-xs bg-white hover:bg-foret-500 hover:text-white text-foret-700 rounded-xl border border-foret-100 shadow-sm transition-all cursor-pointer font-bold whitespace-nowrap active:scale-95"
              >
                {ratio === 1 ? 'SOLDER TOUT' : `${ratio * 100}% – ${formatPrice(Math.round(totalAvecRemise * ratio), false)} F`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Mode paiement */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wider text-[10px]">Mode de paiement</label>
        <div className="flex flex-wrap gap-2">
          {modes.map(mode => (
            <button
              key={mode}
              onClick={(e) => { e.preventDefault(); onChange('modePaiement', mode); }}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer whitespace-nowrap active:scale-95 ${
                paiement.modePaiement === mode
                  ? 'border-foret-500 bg-foret-500 text-white shadow-md'
                  : 'border-gray-100 bg-white text-gray-600 hover:border-foret-300'
              }`}
            >
              <i className={
                mode === 'Espèces' ? 'ri-cash-line mr-1' : 
                mode.includes('Money') || mode === 'Wave' ? 'ri-smartphone-line mr-1' : 
                'ri-bank-card-line mr-1'
              } />
              {mode}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
